import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText, Image as ImageIcon, Zap } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getEventAggregate } from '../../api/events'
import type { EventAggregate } from '../../api/schemas'
import { PageHeader } from '../../components/layout/PageHeader'
import { LevelBadge, StatusBadge, EmptyState } from '../../components/common/Status'
import { TimeAgo } from '../../components/common/TimeAgo'
import { parsePhotoUrls } from '../../lib/format'
import { Skeleton } from '../../components/ui/skeleton'
import { eventTypeLabel } from '../../lib/alert-level'
import { formatCoord, formatMag } from '../../lib/format'
import { RescueFlowPanel } from './RescueFlowPanel'
import { cn } from '../../lib/utils'

type TimelineNode = {
  ts: number
  kind: 'trigger' | 'incident' | 'report'
  title: string
  detail: string
  status?: string
  photos?: string
}

const kindMeta = {
  trigger: { color: 'bg-watch', icon: Zap },
  incident: { color: 'bg-warning', icon: ImageIcon },
  report: { color: 'bg-success', icon: FileText },
} as const

/** 事件聚合详情：三源（触发/直报/报告）合并时间线 */
export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: ['events', 'detail', eventId],
    queryFn: () => getEventAggregate(eventId!),
    enabled: Boolean(eventId),
    retry: 1,
  })

  return (
    <>
      <PageHeader
        breadcrumb="告警流水 / 聚合详情"
        title={query.data?.event.name || `事件 ${eventId ?? ''}`}
        description="聚合触发记录、公众直报与灾情报告的完整时间线"
        actions={
          <button
            onClick={() => void navigate('/events')}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground transition hover:bg-foreground/[.05] hover:text-foreground"
          >
            <ArrowLeft size={13} /> 返回流水
          </button>
        }
      />

      {query.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      ) : query.isError ? (
        <div className="panel-glass rounded-xl">
          <EmptyState title="加载失败" description={query.error.message} />
        </div>
      ) : query.data ? (
        <EventDetailBody data={query.data} />
      ) : (
        <EmptyState title="事件不存在" />
      )}
    </>
  )
}

function EventDetailBody({ data }: { data: EventAggregate }) {
  const { event } = data

  const nodes = useMemo<TimelineNode[]>(() => {
    const list: TimelineNode[] = []
    for (const t of data.triggers) {
      list.push({
        ts: t.createdAt,
        kind: 'trigger',
        title: t.deviceId,
        detail: `IQR ${t.iqr.toFixed(3)} · CAV ${t.cav.toFixed(2)} · 过零率 ${t.zcRate.toFixed(1)} · STA/LTA ${t.staLtaRatio.toFixed(1)}${t.bearing != null ? ` · 方位 ${t.bearing.toFixed(0)}°` : ''}`,
        status: t.confirmed ? 'confirmed' : 'pending',
      })
    }
    for (const inc of data.incidents) {
      list.push({
        ts: inc.createdAt,
        kind: 'incident',
        title: inc.description || inc.reportId,
        detail: `公众直报 · ${formatCoord(inc.lat, inc.lon)}`,
        status: inc.status,
        photos: inc.photoUrls,
      })
    }
    for (const rep of data.reports) {
      list.push({
        ts: rep.createdAt,
        kind: 'report',
        title: rep.title || rep.reportId,
        detail: rep.summary || '灾情报告',
        status: rep.status,
      })
    }
    return list.sort((a, b) => b.ts - a.ts)
  }, [data])

  return (
    <>
      <div className="panel-glass rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-2">
          <LevelBadge alertLevel={event.alertLevel} severity={event.severity} pulse={event.status === 'pending'} />
          <StatusBadge status={event.status} />
          <span className="text-[10px] text-muted-foreground">{eventTypeLabel(event.type)}</span>
          <span className="ml-auto font-mono text-[10px] text-muted-foreground/50">{event.eventId}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
          <BigCell label="震级" value={formatMag(event.magnitude)} tone="text-critical" />
          <BigCell label="深度" value={`${event.depth.toFixed(0)} km`} />
          <BigCell label="触发/确认" value={`${event.triggerCount} / ${event.confirmedDevices}`} />
          <BigCell label="预估到达" value={event.estimatedArrivalSec > 0 ? `${event.estimatedArrivalSec}s` : '—'} tone="text-warning" />
          <BigCell label="位置" value={formatCoord(event.lat, event.lon)} />
        </div>
      </div>

      {/* 活跃事件展示救援流向（任务 / 志愿者轨迹 / 证据）；无 tasks.read 权限时面板自行隐藏 */}
      {event.status !== 'closed' && <RescueFlowPanel eventId={event.eventId} className="mt-4" />}

      <div className="panel-glass mt-4 rounded-xl p-5">
        <h2 className="mb-4 text-sm font-semibold text-foreground">事件时间线（{nodes.length}）</h2>
        {nodes.length === 0 ? (
          <EmptyState title="暂无时间线记录" description="触发/直报/报告数据将按时间倒序展示" />
        ) : (
          <ol>
            {nodes.map((node, i) => {
              const meta = kindMeta[node.kind]
              const Icon = meta.icon
              return (
                <li key={`${node.kind}-${i}-${node.ts}`} className="relative flex gap-3 pb-5 last:pb-0">
                  {i < nodes.length - 1 && <span className="absolute left-[7px] top-5 h-full w-px bg-border" />}
                  <span className={cn('relative z-10 mt-0.5 grid size-[15px] shrink-0 place-items-center rounded-full', meta.color)}>
                    <Icon size={9} className="text-background" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-foreground">{node.title}</span>
                      {node.status && <StatusBadge status={node.status} />}
                      <TimeAgo ts={node.ts} className="ml-auto text-[10px] text-muted-foreground" />
                    </div>
                    <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{node.detail}</div>
                    {node.photos && parsePhotoUrls(node.photos).length > 0 && (
                      <div className="mt-1.5 flex gap-1.5">
                        {parsePhotoUrls(node.photos).slice(0, 4).map((url) => (
                          <img
                            key={url}
                            src={`/uploads/incidents/${url.replace(/^.*\//, '')}`}
                            alt="直报照片"
                            loading="lazy"
                            className="size-14 rounded-md border border-border object-cover"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </div>
    </>
  )
}

function BigCell({ label, value, tone = 'text-foreground' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg bg-black/20 p-3">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={cn('mt-1 font-mono text-sm font-semibold tabular-nums', tone)}>{value}</div>
    </div>
  )
}
